import React from 'react';
import cn from 'classnames';

import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';

import { TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import checkIcon from '@adsk/bim360-matrix-react-assets/check.svg';
import alloyCheckIcon from '@adsk/bim360-matrix-react-assets/alloy-checkmark.svg';
import checkboxIcon from '@adsk/bim360-matrix-react-assets/check-icn-checkbox.svg';
import modalCloseIcon from '@adsk/bim360-matrix-react-assets/modal-close.svg';
import { Themes, ThemeConsumer } from '@adsk/bim360-matrix-react-helpers/es/theme';

export const CREATE_TYPE = '__selectbox_create_option__';
const { Themes: TooltipThemes, Places } = Tooltip;

const checkIconMap = {
  [Themes.MATRIX]: checkIcon,
  [Themes.ALLOY]: alloyCheckIcon
};

export function defaultMenuRenderer({ items, renderItem }) {
  return items.map((item, index) => renderItem({ item, index }));
}

export function defaultValueRenderer(value, { stateAndHelpers }) {
  const { itemToString } = stateAndHelpers;
  return <div className="SelectBox__value-text">{itemToString(value)}</div>;
}

export function defaultValueTagRenderer(item, selectProps) {
  const { disabled, valueKey, labelKey, subtextKey, container, stateAndHelpers } = selectProps;
  const { getValueTagProps } = stateAndHelpers;
  const content = item[labelKey] || item[subtextKey];

  return (
    <div key={item[valueKey]} className="SelectBox__value-tag">
      <OverflowTooltip
        className="SelectBox__value-tag-text"
        tooltipClassName="SelectBox__value-tag-popover"
        tooltipStyle={{ maxWidth: '280px' }}
        content={content}
        container={container}
        place={Places.BOTTOM}
        theme={TooltipThemes.DARK}
      >
        <div className={'SelectBox__tooltip--child'}>{content}</div>
      </OverflowTooltip>
      {!disabled && (
        <IconButton className="SelectBox__value-tag-remove" {...getValueTagProps({ item })}>
          <SvgIcon className="SelectBox__value-tag-remove-icon">{modalCloseIcon}</SvgIcon>
        </IconButton>
      )}
    </div>
  );
}

export function defaultLabelRenderer(label, subtext) {
  return (
    <div className="SelectBox__option-label" title={label}>
      <div className="SelectBox__option-text">{label}</div>
      {!!subtext && (
        <div className="SelectBox__option-subtext" title={subtext}>
          {subtext}
        </div>
      )}
    </div>
  );
}

export function defaultOptionRenderer(option, selected, selectProps) {
  const { labelKey, labelGetter, subtextKey, subtextGetter } = selectProps;
  const label = labelGetter ? labelGetter(option) : option[labelKey];
  const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];

  return defaultLabelRenderer(label, subtext);
}

export function defaultCreateOptionRenderer(option, selected, selectProps) {
  const { labelKey, labelGetter, subtextKey, subtextGetter } = selectProps;

  let label = labelGetter ? labelGetter(option) : option[labelKey];
  if (!selected) label = selectProps[TSymbolProp].translate('CREATE_OPTION', { label });
  const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];

  return defaultLabelRenderer(label, subtext);
}

// eslint-disable-next-line react/prop-types
export function defaultItemRenderer({ item, selected, itemProps, selectProps, selectedKeys }) {
  const {
    multiple,
    optionRenderer = defaultOptionRenderer,
    createOptionRenderer = defaultCreateOptionRenderer
  } = selectProps;

  if (item[CREATE_TYPE] && !selected) {
    return <li {...itemProps}>{createOptionRenderer(item, selected, selectProps)}</li>;
  }

  return (
    <li {...itemProps}>
      {multiple && (
        <SvgIcon
          className={cn('SelectBox__option-checkbox-icon', {
            'SelectBox__option-checkbox-icon--checked': selected
          })}
        >
          {checkboxIcon}
        </SvgIcon>
      )}
      {optionRenderer(item, selected, selectProps, selectedKeys)}
      {!multiple && <span className="SelectBox__option-space" />}
      <ThemeConsumer>
        {({ theme }) =>
          !multiple &&
          selected && (
            <SvgIcon className="SelectBox__option-checkmark-icon">{checkIconMap[theme]}</SvgIcon>
          )
        }
      </ThemeConsumer>
    </li>
  );
}

export function defaultFilterOptions(options, inputValue, selectProps) {
  const search = inputValue.trim().toLowerCase();
  if (!search) return options;

  const { labelKey, labelGetter, subtextKey, subtextGetter } = selectProps;
  return options.filter(option => {
    const label = labelGetter ? labelGetter(option) : option[labelKey];
    const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];
    return (
      (label && label.toLowerCase().indexOf(search) >= 0) ||
      (subtext && subtext.toLowerCase().indexOf(search) >= 0)
    );
  });
}
